'use client';

import { useState, type ReactNode } from 'react';
import { Badge } from '../../../../components/badge';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '../../../../components/typography';
import { Button } from '../../../../components/button';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { WebAppClusterDto } from '@terra/shared-contracts-external-network';
import { WebAppClusterDecisionGroup } from './WebAppClusterDecisionGroup';
import { WebAppClusterMatchHint } from './WebAppClusterMatchHint';
import { WebAppClusterLinkSelect } from './WebAppClusterLinkSelect';
import { AfterFactWebAppClusterActions } from './AfterFactWebAppClusterActions';
import type { WebAppClusterDecision } from './detect-web-apps-confirm';
import type { LinkableWebApplication } from './web-app-types';

const COLLAPSED_MEMBER_COUNT = 2;

export type WebAppClusterCardProps = {
  cluster: WebAppClusterDto;
  decision?: WebAppClusterDecision;
  onDecisionChange?: (decision: WebAppClusterDecision) => void;
  onOpen?: () => void;
  applications?: LinkableWebApplication[];
  linkChoice?: string;
  onLinkChoiceChange?: (value: string) => void;
  onLinkExisting?: (applicationId: string) => void;
  onCreateDraft?: () => void;
  isCreatingDraft?: boolean;
  applicationAction?: ReactNode;
};

export function WebAppClusterCard({
  cluster,
  decision,
  onDecisionChange,
  onOpen,
  applications,
  linkChoice,
  onLinkChoiceChange,
  onLinkExisting,
  onCreateDraft,
  isCreatingDraft,
  applicationAction,
}: WebAppClusterCardProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalInventory.webApp',
  );
  const tShared = useTranslations(
    'designSystem.networkSettings.externalInventory.shared',
  );
  const [membersOpen, setMembersOpen] = useState(false);
  const pageTitle = cluster.pageTitle?.trim();
  const showPageTitle =
    Boolean(pageTitle) && pageTitle !== cluster.canonicalOriginUrl;
  const visibleMembers = membersOpen
    ? cluster.members
    : cluster.members.slice(0, COLLAPSED_MEMBER_COUNT);
  const canToggleMembers = cluster.members.length > COLLAPSED_MEMBER_COUNT;
  const canOpen = Boolean(onOpen && cluster.application);
  const showConfirmLink = Boolean(
    decision === 'link' && onLinkChoiceChange && applications,
  );
  const showAfterFactActions = Boolean(
    !decision &&
      !cluster.application &&
      cluster.verdict === 'CUSTOM' &&
      (onCreateDraft || (onLinkExisting && applications)),
  );

  return (
    <Box
      w="full"
      p={4}
      borderRadius="lg"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.neutral.subtle"
      bg="bg.neutral.default"
      cursor={canOpen ? 'pointer' : undefined}
      onClick={canOpen ? onOpen : undefined}
      css={
        canOpen
          ? {
              '&:hover': {
                bg: 'bg.neutral.hover',
                borderColor: 'border.neutral.muted',
              },
            }
          : undefined
      }
    >
      <VStack gap={3} w="full" alignItems="stretch">
        <HStack
          gap={2}
          w="full"
          alignItems="flex-start"
          justify="space-between"
        >
          <VStack gap={1} alignItems="flex-start" flex={1} minW={0}>
            <HStack gap={2} alignItems="center" maxW="full">
              <BodyStrong
                color="fg.neutral.default"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {cluster.canonicalOriginUrl}
              </BodyStrong>
              {cluster.suggestedApplication ? (
                <WebAppClusterMatchHint
                  suggestion={cluster.suggestedApplication}
                />
              ) : null}
              {cluster.verdict === 'SAAS' ? (
                <Badge
                  variant="outline"
                  size="sm"
                  colorPalette="gray"
                  flexShrink={0}
                >
                  {t('saasBadge')}
                </Badge>
              ) : null}
              {cluster.application?.isDraft ? (
                <Badge
                  variant="fill"
                  size="sm"
                  colorPalette="blue"
                  flexShrink={0}
                >
                  {tShared('draft')}
                </Badge>
              ) : null}
            </HStack>
            {showPageTitle ? (
              <BodyDefault
                color="fg.neutral.muted"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                maxW="full"
              >
                {pageTitle}
              </BodyDefault>
            ) : null}
          </VStack>
          {cluster.application && applicationAction ? (
            <Box
              flexShrink={0}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {applicationAction}
            </Box>
          ) : null}
        </HStack>

        <VStack gap={1} alignItems="flex-start" minW={0}>
          <MetaDefault color="fg.muted">
            {t('hostCount', { count: cluster.members.length })}
          </MetaDefault>
          {cluster.members.length > 1
            ? visibleMembers.map((member) => (
                <MetaDefault
                  key={member.originUrl}
                  color="fg.neutral.muted"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  maxW="full"
                >
                  {member.originUrl}
                </MetaDefault>
              ))
            : null}
          {canToggleMembers ? (
            <Button
              variant="link"
              size="sm"
              p={0}
              onClick={(event) => {
                event.stopPropagation();
                setMembersOpen((open) => !open);
              }}
            >
              {membersOpen ? tShared('showLess') : tShared('showMore')}
            </Button>
          ) : null}
        </VStack>
        {decision && onDecisionChange ? (
          <WebAppClusterDecisionGroup
            value={decision}
            onValueChange={onDecisionChange}
          />
        ) : null}
        {showConfirmLink ? (
          <WebAppClusterLinkSelect
            applications={applications ?? []}
            value={linkChoice ?? ''}
            onValueChange={(nextValue) => {
              onLinkChoiceChange?.(nextValue);
            }}
          />
        ) : null}
        {showAfterFactActions ? (
          <AfterFactWebAppClusterActions
            applications={applications ?? []}
            suggestion={cluster.suggestedApplication}
            onLinkExisting={onLinkExisting}
            onCreateDraft={onCreateDraft}
            isCreatingDraft={isCreatingDraft}
          />
        ) : null}
      </VStack>
    </Box>
  );
}
